export type VehicleType =
  "petrol" | "diesel" | "petrol_hybrid" | "electric" | "other";

export const NZ_2026_KM_RATES: Record<
  Exclude<VehicleType, "other">,
  [number, number]
> = {
  petrol: [120, 37],
  diesel: [130, 38],
  petrol_hybrid: [90, 24],
  electric: [122, 23],
};

export function nzIncomeYear(date: string | Date) {
  const value = typeof date === "string" ? new Date(`${date}T12:00:00Z`) : date;
  const month = value.getUTCMonth() + 1;
  return month <= 3 ? value.getUTCFullYear() : value.getUTCFullYear() + 1;
}

export function nzKilometreRate(vehicleType: VehicleType, tier: 1 | 2) {
  if (vehicleType === "other") return 0;
  return NZ_2026_KM_RATES[vehicleType][tier - 1]!;
}

export type DepreciableAsset = {
  depreciable_cost_cents: number;
  depreciation_method: "diminishing_value" | "straight_line" | "none";
  depreciation_rate: number;
  business_use_percent: number;
  investment_boost: boolean;
  in_service_date: string;
  disposal_date: string | null;
};

export type DepreciationEntry = {
  income_year: number;
  closing_adjusted_value_cents: number;
};

function monthsInclusive(start: Date, end: Date) {
  return Math.max(
    0,
    (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
      end.getUTCMonth() -
      start.getUTCMonth() +
      1,
  );
}

export function depreciationEstimate(
  asset: DepreciableAsset,
  priorEntries: DepreciationEntry[],
  incomeYear: number,
) {
  const yearStart = new Date(Date.UTC(incomeYear - 1, 3, 1));
  const yearEnd = new Date(Date.UTC(incomeYear, 2, 31));
  const serviceDate = new Date(`${asset.in_service_date}T12:00:00Z`);
  const disposalDate = asset.disposal_date
    ? new Date(`${asset.disposal_date}T12:00:00Z`)
    : null;
  const activeStart = serviceDate > yearStart ? serviceDate : yearStart;
  const activeEnd =
    disposalDate && disposalDate < yearEnd ? disposalDate : yearEnd;
  const monthsUsed =
    activeStart <= activeEnd
      ? Math.min(monthsInclusive(activeStart, activeEnd), 12)
      : 0;
  const previous = [...priorEntries]
    .filter((entry) => entry.income_year < incomeYear)
    .sort((a, b) => b.income_year - a.income_year)[0];
  const openingAdjustedValueCents =
    previous?.closing_adjusted_value_cents ?? asset.depreciable_cost_cents;
  const firstRecordedYear = !previous;
  const investmentBoostEligible =
    asset.investment_boost &&
    firstRecordedYear &&
    serviceDate >= new Date(Date.UTC(2025, 4, 22)) &&
    nzIncomeYear(asset.in_service_date) === incomeYear;
  const investmentBoostCents = investmentBoostEligible
    ? Math.round(asset.depreciable_cost_cents * 0.2)
    : 0;
  const afterBoost = Math.max(
    openingAdjustedValueCents - investmentBoostCents,
    0,
  );
  const annualBase =
    asset.depreciation_method === "straight_line"
      ? Math.max(
          asset.depreciable_cost_cents -
            (investmentBoostEligible
              ? Math.round(asset.depreciable_cost_cents * 0.2)
              : 0),
          0,
        )
      : afterBoost;
  const depreciationCents =
    asset.depreciation_method === "none" || monthsUsed === 0
      ? 0
      : Math.min(
          Math.round(
            annualBase * (asset.depreciation_rate / 100) * (monthsUsed / 12),
          ),
          afterBoost,
        );
  const businessShare = asset.business_use_percent / 100;

  return {
    incomeYear,
    monthsUsed,
    openingAdjustedValueCents,
    investmentBoostCents,
    investmentBoostClaimCents: Math.round(investmentBoostCents * businessShare),
    depreciationCents,
    depreciationClaimCents: Math.round(depreciationCents * businessShare),
    closingAdjustedValueCents: Math.max(afterBoost - depreciationCents, 0),
  };
}

export function parseQuoteItems(value: string) {
  const items = value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [
        description = "",
        quantityText = "1",
        unitText = "0",
        gstText = "15",
      ] = line.split("|").map((part) => part.trim());
      const quantity = Number(quantityText);
      const unitAmount = Number(unitText);
      const gstRate = Number(gstText);
      if (!description || !Number.isFinite(quantity) || quantity <= 0) {
        throw new Error(`Check quote line ${index + 1}.`);
      }
      if (!Number.isFinite(unitAmount) || unitAmount < 0) {
        throw new Error(`Check the amount on quote line ${index + 1}.`);
      }
      if (!Number.isFinite(gstRate) || gstRate < 0 || gstRate > 100) {
        throw new Error(`Check GST on quote line ${index + 1}.`);
      }
      return {
        description,
        quantity,
        unit_amount_cents: Math.round(unitAmount * 100),
        gst_rate: gstRate,
        sort_order: index,
      };
    });
  if (items.length === 0) throw new Error("Add at least one quote line.");
  return items;
}
